const ENGLISH_MONTH_TO_NUMBER = {
  jan: "01",
  january: "01",
  feb: "02",
  february: "02",
  mar: "03",
  march: "03",
  apr: "04",
  april: "04",
  may: "05",
  jun: "06",
  june: "06",
  jul: "07",
  july: "07",
  aug: "08",
  august: "08",
  sep: "09",
  sept: "09",
  september: "09",
  oct: "10",
  october: "10",
  nov: "11",
  november: "11",
  dec: "12",
  december: "12",
} as const;

function padDateUnit(value: string | number) {
  return String(value).padStart(2, "0");
}

function toEnglishMonthNumber(rawMonth: string) {
  return ENGLISH_MONTH_TO_NUMBER[
    rawMonth.replace(/\./g, "").toLowerCase() as keyof typeof ENGLISH_MONTH_TO_NUMBER
  ];
}

function isValidMonthDay(month: number, day: number) {
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

export function normalizeChineseFriendlyDateText(input: string) {
  if (!input.trim()) {
    return input;
  }

  return input
    .replace(/\b(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})\b/g, (match, year, month, day) => {
      const numericMonth = Number(month);
      const numericDay = Number(day);
      if (!isValidMonthDay(numericMonth, numericDay)) {
        return match;
      }
      return `${year}/${padDateUnit(month)}/${padDateUnit(day)}`;
    })
    .replace(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g, (match, month, day, year) => {
      const numericMonth = Number(month);
      const numericDay = Number(day);
      if (!isValidMonthDay(numericMonth, numericDay)) {
        return match;
      }
      return `${year}/${padDateUnit(month)}/${padDateUnit(day)}`;
    })
    .replace(
      /\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,\s*|\s+)(\d{4})\b/gi,
      (match, monthName, day, year) => {
        const month = toEnglishMonthNumber(monthName);
        const numericDay = Number(day);
        if (!month || !isValidMonthDay(Number(month), numericDay)) {
          return match;
        }
        return `${year}/${month}/${padDateUnit(day)}`;
      },
    )
    .replace(
      /\b(\d{1,2})(?:st|nd|rd|th)?\s+(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?,?\s+(\d{4})\b/gi,
      (match, day, monthName, year) => {
        const month = toEnglishMonthNumber(monthName);
        const numericDay = Number(day);
        if (!month || !isValidMonthDay(Number(month), numericDay)) {
          return match;
        }
        return `${year}/${month}/${padDateUnit(day)}`;
      },
    );
}

export function formatNewsUtcDate(
  value: string | Date,
  options?: {
    includeYear?: boolean;
    includeTime?: boolean;
  },
) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const includeYear = options?.includeYear ?? true;
  const includeTime = options?.includeTime ?? false;
  const year = date.getUTCFullYear();
  const month = padDateUnit(date.getUTCMonth() + 1);
  const day = padDateUnit(date.getUTCDate());
  const dateText = includeYear ? `${year}/${month}/${day}` : `${month}/${day}`;

  if (!includeTime) {
    return dateText;
  }

  const hour = padDateUnit(date.getUTCHours());
  const minute = padDateUnit(date.getUTCMinutes());
  return `${dateText} ${hour}:${minute} UTC`;
}
